import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { IonicModule } from '@ionic/angular'
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco'
import { AsyncPipe } from '@angular/common'
import { ButtonModule } from 'primeng/button'
import { ConfirmationService } from 'primeng/api'
import { EditorAssistanceBundle } from '../../EditorAssistanceBundle'
import { EditorAssistance } from '../../../../../models/EditorAssistance'
import { ClientSelectComponent } from '../../../assistance-editor/client-select/client-select.component'
import { AssistanceCareLogEditorComponent } from '../../../assistance-care-log-editor/assistance-care-log-editor.component'
import { CareLogEditorAssistant } from '../../../../../models/assistance-care-log.model'
import { GroupSessionService } from '../group-session.service'
import { AssistanceEditorModalEditableHandler } from '../../AssistanceEditorModalEditableHandler'
import { AssistanceEditorModalService } from '../../assistance-editor-modal.service'

const EMPTY_ASSIGNMENTS: EditorAssistance['assignments'] = []

/**
 * Component for multiple client selection in group session assistance.
 */
@Component({
  selector: 'app-group-session-clients',
  templateUrl: './group-session-clients.component.html',
  styleUrls: ['./group-session-clients.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    IonicModule,
    TranslocoPipe,
    AsyncPipe,
    ButtonModule,
    ClientSelectComponent,
    AssistanceCareLogEditorComponent,
  ],
})
export class GroupSessionClientsComponent {
  private groupSessionService = inject(GroupSessionService)
  private modalService = inject(AssistanceEditorModalService)
  private confirmationService = inject(ConfirmationService)
  private transloco = inject(TranslocoService)
  private cdr = inject(ChangeDetectorRef)

  @Input({ required: true }) bundle!: EditorAssistanceBundle
  @Input({ required: true }) editableHandler!: AssistanceEditorModalEditableHandler

  @ViewChild('clientSelect') private clientSelect!: ClientSelectComponent

  /** Emits the main assistance URID when the bundle membership changed and the host should reload it. */
  @Output() reloadBundle = new EventEmitter<string>()

  protected busy = false
  private expandedMemberIds = new Set<number>()
  private careLogAssignmentsCache: { source: unknown; result: CareLogEditorAssistant[] } | null = null

  /** Mirrors the parent editor's editable state — a group session member's care log follows the same guard. */
  get editable$() {
    return this.editableHandler.getEditable()
  }

  get members(): EditorAssistance[] {
    return this.bundle?.all ?? []
  }

  get memberClientIds(): number[] {
    return this.members.map((m) => m.clientId).filter((id): id is number => !!id)
  }

  /** Cached by source reference — a fresh array on every template check would refetch each care-log editor's logs constantly. */
  get careLogAssignments(): CareLogEditorAssistant[] {
    const source = this.bundle?.main?.assignments ?? EMPTY_ASSIGNMENTS
    if (this.careLogAssignmentsCache?.source === source) return this.careLogAssignmentsCache.result

    const result = source.map((a) => ({
      assignmentId: a.id,
      assistantId: a.assistantId,
      fullname: a.fullname,
    }))
    this.careLogAssignmentsCache = { source, result }
    return result
  }

  isPrimary(member: EditorAssistance): boolean {
    return this.groupSessionService.isPrimaryMember(member)
  }

  isCareLogExpanded(member: EditorAssistance): boolean {
    return this.expandedMemberIds.has(member.id)
  }

  toggleCareLog(member: EditorAssistance): void {
    if (this.expandedMemberIds.has(member.id)) {
      this.expandedMemberIds.delete(member.id)
    } else {
      this.expandedMemberIds.add(member.id)
    }
    this.cdr.markForCheck()
  }

  /** Adds the client chosen in the picker modal immediately (the "Přidat klienta" button opens the picker). */
  async onClientPicked(clientId: number): Promise<void> {
    if (!clientId || this.busy) return
    this.busy = true
    try {
      const parentId = await this.groupSessionService.addClient(this.bundle.main, clientId)
      if (parentId != null) this.reloadBundle.emit(parentId.toString())
    } finally {
      this.busy = false
      // Otherwise picking the same client again after a blocked attempt (e.g. an overlap) is a silent
      // no-op — ionic-selectable only fires a change event when the picked value actually differs.
      this.clientSelect?.reset()
      this.cdr.detectChanges()
    }
  }

  async onRemoveMember(member: EditorAssistance): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      const isPrimary = this.groupSessionService.isPrimaryMember(member)
      const remainingChildren = this.bundle.children.filter((c) => c.URID !== member.URID)
      const removesWholeAssistance = isPrimary && remainingChildren.length === 0

      if (removesWholeAssistance && !(await this.confirmRemoveLastMember())) return

      const removed = await this.groupSessionService.removeMember(member)
      if (!removed) return

      if (removesWholeAssistance) {
        // No members left: the whole assistance was deleted on the backend, not just this member.
        this.modalService.mainAssistanceDeletedInCurrentlyOpenEditor()
        return
      }

      // Removing the primary promotes the earliest remaining child to take its place (see
      // AssistanceHelper#prepareBundleParentRemoval); removing a child leaves the primary untouched either way.
      const reloadTarget = isPrimary ? [...remainingChildren].sort((a, b) => a.id - b.id)[0] : this.bundle.main
      this.reloadBundle.emit(reloadTarget.URID)
    } finally {
      this.busy = false
      this.cdr.detectChanges()
    }
  }

  private confirmRemoveLastMember(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: this.transloco.translate('GROUP_SESSION_EDITOR.REMOVE_LAST_CONFIRM_HEADER'),
        message: this.transloco.translate('GROUP_SESSION_EDITOR.REMOVE_LAST_CONFIRM_MESSAGE'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.transloco.translate('COMMON.DELETE'),
        rejectLabel: this.transloco.translate('COMMON.CANCEL'),
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => resolve(true),
        reject: () => resolve(false),
      })
    })
  }
}
